import {
  createEmotionCache,
  MantineProvider,
  type MantineTheme,
} from '@mantine/core';
import { tippy } from '@tippyjs/react';
import React, {
  lazy,
  Suspense,
  useContext,
  useEffect,
  useReducer,
  useState,
} from 'react';
import Helmet from 'react-helmet';
import { IntlProvider } from 'react-intl';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { of, Subject } from 'rxjs';
import { ajax } from 'rxjs/ajax';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { createGlobalStyle, ThemeProvider } from 'styled-components/macro';
import rtlPlugin from 'stylis-plugin-rtl';
import { SWRConfig } from 'swr';

import { UserContext } from './context/user';

import FullPageLoader from './components/loader/full-page-loader';
import { showLoader } from './components/loader/showLoader';
import Modal from './components/modal/modal';

import routes from './routes';
import messages_en from './translations/en';
import messages_fa from './translations/fa';

import {  mapAPIKey, setMapAPIKey } from './configs/config';
import URL from './configs/URL';
import { ChangeLang$$ } from './utils/changeLang';

import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import 'jsoneditor/dist/jsoneditor.css';
import 'react-toastify/dist/ReactToastify.css';
import 'tippy.js/dist/tippy.css';
import './ReactToastify-customize.css';

import { tokenState } from 'atoms/general';
import { useRecoilValue } from 'recoil';
import './App.css';

import { Font } from '@react-pdf/renderer';

// @ts-ignore
import IRANSansX_Regular from 'assets/fonts/woff/IRANSansX-Regular.woff';
// @ts-ignore
import IRANSansX_Bold from 'assets/fonts/woff/IRANSansX-Bold.woff';
// @ts-ignore
import IRANSansXFaNum_Regular from 'assets/fonts/woff/IRANSansXFaNum-Regular.woff';

Font.register({
  family: 'IRANSansX-Regular',
  src: IRANSansX_Regular,
});

Font.register({
  family: 'IRANSansX-Bold',
  src: IRANSansX_Bold,
});

Font.register({
  family: 'IRANSansXFaNum-Regular',
  src: IRANSansXFaNum_Regular,
});

const MainLayout = lazy(() => import('./layouts/main/main'));
const LoginLayout = lazy(() => import('./layouts/login/login'));
const FinalMapLayout = lazy(() => import('./layouts/final-map/final-map'));
const ProfileLayout = lazy(() => import('./layouts/profile/profile'));
const ShareViewLayout = lazy(() => import('./layouts/share/share'));

tippy.setDefaultProps({
  animation: 'fade',
  arrow: false,
});

const _routes: Record<string, string[]> = {};
Object.keys(routes).forEach((route: string) => {
  _routes[route] = routes[route].map((item) => item.path);
});

const themes = {
  light: {
    primary: '#ea4c89',
    secondry: '#2e0767',
    secondary_1: '#D7E1F9',
    secondary_2: '#6875D9',
    shade_1: '#1c1c1c',
    shade_2: '#949494',
    shade_3: '#c2c2c2',
    shade_4: '#f6f6f6',
    shade_5: '#fafafa',
    light_1: '#ffffff',
    success: '#20A76E',
    // success: '#3CDAA7',
    error: '#D10328',
    // error: '#EF263C',
    warning: '#F5D600',
    success_light: '#F1FFFA',
    error_light: '#FFF2F4',
    warning_light: '#FFFFF1',

    black: '#101828',
  },
  dark: {},
} as const;

const GlobalStyling = createGlobalStyle`
  :root {
    --color-primary: ${themes.light.primary};
    --color-secondry: ${themes.light.secondry};
    --color-secondary-1: ${themes.light.secondary_1};
    --color-secondary-2: ${themes.light.secondary_2};
    --shade-1: ${themes.light.shade_1};
    --shade-2: ${themes.light.shade_2};
    --shade-3: ${themes.light.shade_3};
    --shade-4: ${themes.light.shade_4};
    --shade-5: ${themes.light.shade_5};
    --light-1: ${themes.light.light_1};
    --success: ${themes.light.success};
    --error: ${themes.light.error};
    --warning: ${themes.light.warning};
    --success-light: ${themes.light.success_light};
    --error-light: ${themes.light.error_light};
    --warning-light: ${themes.light.warning_light};
    --black: ${themes.light.black};

    --font-family: 'IRANSansWeb'
  }
`;

const rtlCache = createEmotionCache({
  key: 'mantine-rtl',
  stylisPlugins: [rtlPlugin],
});

const mantineTheme = {
  fontFamily: 'IRANSansWeb',
  primaryColor: 'pink',
} as MantineTheme;

const messages: { [key: string]: TranslationMessages } = {
  en: messages_en,
  fa: messages_fa,
};

interface TranslationMessages {
  [key: string]: string;
}

function App() {
  const location = useLocation();
  const history = useHistory();

  const token = useRecoilValue(tokenState);

  const { permissions, setMyPermissions, setUserData }: any =
    useContext(UserContext);
  const [theme] = useState('light');
  const [lang, changeLang] = useReducer<React.Reducer<string, any>, string>(
    (oldLang: string, newLag: string) => {
      localStorage['lang'] = newLag;
      return newLag;
    },
    'fa',
    (initial) => {
      return localStorage['lang'] || 'fa';
    }
  );
  const dir = lang === 'en' ? 'ltr' : 'rtl';

  const checkValidation = new Subject();
  checkValidation
    .pipe(
      switchMap(() => {
        showLoader.sendMessage('true');
        return ajax({
          url: `${URL.validation}`,
          method: 'POST',
          headers: {
            'x-api-key': mapAPIKey,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token: token,
          }),
        }).pipe(
          mergeMap(() => {
            return ajax({
              url: `${URL.profile}`,
              method: 'GET',
              headers: {
                token: token,
                'x-api-key': mapAPIKey,
                'Content-Type': 'application/json',
              },
            });
          }),
          map(({ response }) => {
            const tempRole = response.roles.find(
              (role: any) => role.scope === 'my'
            );
            if (tempRole) {
              setUserData({
                id: response.id,
                name: response.name,
                username:
                  localStorage['login_otp'] === 'true'
                    ? response.mobile
                    : response.email,
                role:
                  localStorage['lang'] === 'fa'
                    ? tempRole.title
                    : tempRole.name,
              });
              setMyPermissions?.(
                response.roles?.map((role: any) => role?.permissions).flat()
              );
              if (response?.my_app?.access_token?.token) {
                setMapAPIKey(response.my_app.access_token.token);
              }
            } else {
              history.replace('/login');
              localStorage.removeItem('sessionToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('user_id');
              localStorage.removeItem('isLoggedIn');
            }
          }),
          catchError((error) => {
            showLoader.sendMessage('false');
            history?.replace(
              `/login?redirect_to=${location.pathname + location.search}`
            );
            return of(error);
          })
        );
      })
    )
    .subscribe();

  const checkLogin = () => {
    if (
      localStorage.getItem('isLoggedIn') !== 'true' &&
      location.pathname !== '/login' &&
      !location.pathname.startsWith(`/share`)
    ) {
      history?.replace('/login');
    } else if (
      localStorage.getItem('isLoggedIn') === 'true' &&
      location.pathname !== '/login' &&
      !location.pathname.startsWith(`/share`)
    )
      checkValidation.next();
  };

  useEffect(() => {
    checkLogin();

    const MessageBus = ChangeLang$$.onMessage();
    const subscription = MessageBus.subscribe({
      next: function (lang: string) {
        changeLang(lang);
      },
    });

    return () => subscription.unsubscribe();
  }, []);

  if (
    !permissions &&
    location.pathname !== '/login' &&
    !location.pathname.startsWith(`/share`)
  )
    return <FullPageLoader />;
  else
    return (
      <MantineProvider
        withCSSVariables
        emotionCache={rtlCache}
        theme={{ ...mantineTheme, dir }}
      >
        <ThemeProvider theme={themes[theme as keyof typeof themes]}>
          <IntlProvider locale={lang} key={lang} messages={messages[lang]}>
            <GlobalStyling />
            <div className="App" dir={dir}>
              <Helmet htmlAttributes={{ lang: lang, dir }} />

              <Modal />

              <SWRConfig
                value={{
                  revalidateOnFocus: false,
                  revalidateIfStale: false,
                  shouldRetryOnError: false,
                }}
              >
                <Suspense fallback={<FullPageLoader />}>
                  <Switch>
                    <Route
                      path={_routes['main']}
                      exact={true}
                      component={MainLayout}
                    />
                    <Route
                      path={_routes['login']}
                      exact={true}
                      component={LoginLayout}
                    />
                    <Route
                      path={_routes['finalMap']}
                      exact={true}
                      component={FinalMapLayout}
                    />
                    <Route
                      path={_routes['profile']}
                      exact={true}
                      component={ProfileLayout}
                    />
                    <Route
                      path={_routes['share']}
                      exact={false}
                      component={ShareViewLayout}
                    />
                    <Redirect to={`/`} />
                  </Switch>
                </Suspense>
              </SWRConfig>

              <ToastContainer
                containerId="toastify"
                limit={2}
                draggable={false}
                closeButton={false}
                position="bottom-center"
                rtl={lang !== 'en'}
                autoClose={1000}
                hideProgressBar
              />
            </div>
          </IntlProvider>
        </ThemeProvider>
      </MantineProvider>
    );
}

export default App;
